import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import getTenantIdFromURL from "../utils/getTenantIdFromURL";
import { useSelector } from "react-redux";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Card,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Pagination,
  PaginationItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  DataGridPremium,
  GridFooterContainer,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  useGridSelector,
  gridFilteredTopLevelRowCountSelector,
  gridDensityValueSelector
} from "@mui/x-data-grid-premium";
import "./RecommendationTable.css";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { LocalStyle } from "./helper";
import { ColorEnum } from "../Assets/Colors/ColorsEnum";
import { PieChart, Pie } from "recharts";
import CircleIcon from "@mui/icons-material/Circle";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { CSVLink } from "react-csv";
import {
  useGridApiContext,
  gridFilteredSortedRowIdsSelector,
  gridFilterModelSelector,
  getGridSingleSelectOperators,
  getGridStringOperators 
} from "@mui/x-data-grid-premium";
import DownloadIcon from "@mui/icons-material/Download";
import newSearchIcon from "../../src/Assets/Images/searchIconNew.svg";
import { useProspectsAR } from "../context/prospectsARContext";
import { constrainValue, formatCurrency, formatCurrencyValue, formatArcScore, normalizePaydex, processAgingData, MitigationOptions, getScoreAttributes, isUnderwritingActive } from "../utils/Helper";
import { insuredLimitFor } from "../utils/insuredLimit";
import SendRequestModal from "../SendRequestModal";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { startUnderwritingFlow, updateCompany } from "../api/companyApi";
import { updateRequestedLimit, updateCompanySummary } from "../api/underwritingApi";
import { getFreezeMessage } from "../utils/refreshFreeze";
import { markRefreshing, clearRefreshing, isRefreshing, refreshStartedAt, subscribeRefreshing, MAX_REFRESH_MS } from "../utils/refreshRegistry";
import { track } from "../analytics/analytics";

const Legend = () => (
  <Box display="flex" alignItems="center" padding={1}>
    <CircleIcon sx={{ color: "#3097D2", fontSize: 12, mr: 2 }} />
    <Typography>Public Company</Typography>
  </Box>
);

// Shown in a table cell while a company's underwriting is still running (a just-added
// company, or one being re-underwritten). Used in both the AR and Prospects tables.
const UnderwritingCell = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <CircularProgress size={15} thickness={5} sx={{ color: "#5186EC" }} />
    <Typography sx={{ fontSize: 12, color: "#5186EC", fontStyle: "italic", whiteSpace: "nowrap" }}>
      Underwriting…
    </Typography>
  </Box>
);

const parseUnderwritingDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  if (typeof value?._seconds === 'number') return new Date(value._seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLastUnderwritingDate = (value, includeTime = false) => {
  const date = parseUnderwritingDate(value);
  if (!date) return 'Not yet';
  return includeTime
    ? date.toLocaleString()
    : date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

// Helper function to safely render headers
const safeRenderHeader = (params, fallbackHeaderName) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
    >
      <Typography
        sx={{
          whiteSpace: "normal",
          lineHeight: "1.5",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontFamily: "rubik",
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {params?.colDef?.headerName || fallbackHeaderName}
      </Typography>
    </Box>
  );
};

// DoughnutChart component
const DoughnutChart = ({ row }) => {
  const data = [
    { name: "Country Score", value: row["Country Score"], fill: "#2F3D63" },
    { name: "Legal Score", value: row["Legal Score"], fill: "#3AA623" },
    {
      name: "Deliquency Score",
      value: row["Deliquency Score"],
      fill: "#F25A48",
    },
    { name: "Failure Score", value: row["Failure Score"], fill: "#E7BF3C" },
    { name: "Altman Z Score", value: row["Altman Z Score"], fill: "#F248B4" },
    {
      name: "Pulse Index",
      value: row["Alternative Score"],
      fill: "#5186EC",
    },
    { name: "Sector Score", value: row["Sector Score"], fill: "#3AA629" },
  ];

  return (
    <PieChart height={50} width={50}>
      <Pie
        data={data}
        spacing={2}
        // cx={60}
        // cy={100}
        innerRadius={15}
        outerRadius={25}
        fill="#8884d8"
        paddingAngle={0}
        dataKey="value"
      ></Pie>
    </PieChart>
  );
};
const CustomFooter = React.forwardRef((props, ref) => {
  const theme = useTheme();
  const { paginationModel, onPaginationModelChange, density } = props;
  const apiRef = useGridApiContext();
  const filteredRowCount = useGridSelector(apiRef, gridFilteredTopLevelRowCountSelector);

  const pageSizeForDensity = useMemo(() => ({
    comfortable: 5,
    standard: 7,
    compact: 10,
  }), []);

  const pageSize = pageSizeForDensity[density] ?? paginationModel?.pageSize ?? 10;


  const handlePageChange = (event, newPage) => {
    if (onPaginationModelChange) {
      onPaginationModelChange({ ...paginationModel, page: newPage - 1 });
    }
  };

  const handleDensityChange = useCallback((newDensity) => {
    const newPageSize = pageSizeForDensity[newDensity];
    if (onPaginationModelChange) {
      onPaginationModelChange({ pageSize: newPageSize, page: 0 });
    }
  }, [pageSizeForDensity, onPaginationModelChange]);

  React.useEffect(() => {
    handleDensityChange(density);
  }, [density, handleDensityChange]);

  const pageCount = Math.ceil(filteredRowCount / pageSize);

  return (
    <GridFooterContainer ref={ref}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding={1}
        sx={{ borderTop: `1px solid ${theme.palette.divider}`, width: '100%' }}
      >
        <Legend />
        <Box display="flex" alignItems="center">
          <Typography variant="body2" sx={{ marginRight: 2 }}>
            Total Companies: {filteredRowCount}
          </Typography>
          <Pagination
            count={pageCount}
            page={paginationModel?.page + 1}
            onChange={handlePageChange}
            shape="rounded"
            renderItem={(item) => (
              <PaginationItem
                slots={{
                  previous: () => (
                    <Box
                      sx={{
                        display: "flex",
                        "&:hover .hoverText": { textDecoration: "underline" },
                      }}
                    >
                      <KeyboardArrowLeftIcon />
                      <Typography className="hoverText">Previous</Typography>
                    </Box>
                  ),
                  next: () => (
                    <Box
                      sx={{
                        display: "flex",
                        "&:hover .hoverText": { textDecoration: "underline" },
                      }}
                    >
                      <Typography className="hoverText">Next</Typography>
                      <KeyboardArrowRightIcon />
                    </Box>
                  ),
                }}
                {...item}
              />
            )}
            sx={{
              fontFamily: "rubik",
              "& .MuiPaginationItem-root": {
                color: "#1A2A56",
              },
              ".MuiPaginationItem-page.Mui-selected": {
                backgroundColor: "#1A2A56",
                ":hover": { backgroundColor: "#1A2A56" },
                color: "white",
              },
            }}
          />
        </Box>
      </Box>
    </GridFooterContainer>
  );
});


// Inline-editable "Amount Requested" cell. Persists the new requested credit
// limit to the company record (the same fields ExecutiveSummary writes), so a
// subsequent refresh re-runs underwriting against the updated amount.
const EditableRequestedAmount = ({ row }) => {
  const reduxTenant = useSelector((state) => state.userInfo.tenantId);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState(row?.["Requested Limit"] ?? null);
  const [draft, setDraft] = useState("");

  // Re-sync when the grid re-fetches this row.
  useEffect(() => { setAmount(row?.["Requested Limit"] ?? null); }, [row]);

  const startEdit = (e) => {
    e.stopPropagation();
    setDraft(amount != null && amount !== "" ? String(amount) : "");
    setEditing(true);
  };
  const cancel = (e) => { if (e) e.stopPropagation(); setEditing(false); };

  const save = async (e) => {
    if (e) e.stopPropagation();
    const newLimit = Number(String(draft).replace(/[^0-9.]/g, "")) || 0;
    const tenantId = getTenantIdFromURL() || reduxTenant;
    const companyId = row?.companyId;
    if (!tenantId || !companyId) { toast.error("Couldn't identify the company"); return; }
    const oldLimit = Number(amount) || 0;
    const recommended = Number(row?.["Recommended Limit"]) || 0;

    setSaving(true);
    try {
      // Same persistence ExecutiveSummary performs on a requested-limit change.
      await updateRequestedLimit(tenantId, companyId, oldLimit, newLimit, recommended);
      await updateCompany(companyId, { tenantId, requestedCreditLimit: newLimit });
      await updateCompanySummary(tenantId, companyId, { approvalStatus: "PENDING", approvedLimit: 0 });
      setAmount(newLimit);
      setEditing(false);
      toast.success("Amount requested updated — refresh the company to re-underwrite");
    } catch (err) {
      console.error("Update requested amount failed:", err);
      toast.error("Failed to update amount requested");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <TextField
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(e); if (e.key === "Escape") cancel(e); }}
          autoFocus
          size="small"
          type="number"
          placeholder="0"
          disabled={saving}
          InputProps={{ startAdornment: <InputAdornment position="start" sx={{ mr: 0.25 }}>$</InputAdornment> }}
          sx={{ width: 118, "& .MuiInputBase-input": { py: "6px", fontSize: 13 } }}
        />
        <Tooltip title="Save" arrow>
          <span>
            <IconButton size="small" onClick={save} disabled={saving} sx={{ color: "#12A150" }}>
              {saving ? <CircularProgress size={15} /> : <CheckIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Cancel" arrow>
          <IconButton size="small" onClick={cancel} disabled={saving} sx={{ color: "#94a3b8" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  const display = formatCurrency(amount) !== "$0" ? formatCurrency(amount) : "-";
  return (
    <Tooltip title="Click to edit amount requested" arrow>
      <Box
        onClick={startEdit}
        sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", width: "100%", "&:hover .edit-pencil": { opacity: 1 } }}
      >
        <span>{display}</span>
        <EditIcon className="edit-pencil" sx={{ fontSize: 15, color: "#5186EC", opacity: 0, transition: "opacity .15s" }} />
      </Box>
    </Tooltip>
  );
};

// Per-company refresh — re-runs the AI underwriting flow for a single row.
const RefreshCompanyButton = ({ row }) => {
  const reduxTenant = useSelector((state) => state.userInfo.tenantId);
  const companyId = row?.companyId;
  const [, setTick] = useState(0);
  const wasActiveRef = useRef(false);

  // Re-render this button whenever the shared refresh registry changes, so the
  // spinner survives DataGrid cell re-mounts and reflects the whole run.
  useEffect(() => subscribeRefreshing(() => setTick((t) => t + 1)), []);

  const active = isUnderwritingActive(row);
  const marked = isRefreshing(companyId);
  const spinning = marked || active;

  // Clear the marker when the run genuinely completes: we saw it go active and
  // then inactive (poll picked up underwritingInProgress → true → false).
  useEffect(() => {
    if (active) wasActiveRef.current = true;
    if (!active && wasActiveRef.current && marked) {
      clearRefreshing(companyId);
      wasActiveRef.current = false;
    }
  }, [active, marked, companyId]);

  // Safety net: if the run finished between polls (never observed as active) or
  // the flag never surfaced, drop the marker after a run's worth of time.
  useEffect(() => {
    if (!marked) return;
    const started = refreshStartedAt(companyId) || Date.now();
    const remaining = Math.max(0, MAX_REFRESH_MS - (Date.now() - started));
    const t = setTimeout(() => clearRefreshing(companyId), remaining);
    return () => clearTimeout(t);
  }, [marked, companyId]);

  const handleRefresh = async (e) => {
    e.stopPropagation();
    if (spinning) return;

    // Respect a per-company freeze (backend is still the source of truth).
    const freezeBlock = getFreezeMessage(row);
    if (freezeBlock) {
      toast.warning(freezeBlock);
      return;
    }

    const tenantId = getTenantIdFromURL() || reduxTenant;
    if (!tenantId || !companyId) {
      toast.error("Couldn't identify the company to refresh");
      return;
    }

    wasActiveRef.current = false;
    markRefreshing(companyId); // starts spinning immediately + tells the page to keep polling
    track("company_refresh", { companyId, companyName: row["Company Name"] || null });
    try {
      await startUnderwritingFlow(tenantId, companyId);
      toast.success(`Refreshing ${row["Company Name"] || "company"}…`);
    } catch (err) {
      console.error("Row refresh failed:", err);
      toast.error("Failed to start refresh");
      clearRefreshing(companyId);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
      <Tooltip title="Re-run AI underwriting" arrow>
        <span>
        <IconButton
          onClick={handleRefresh}
          disabled={spinning}
          size="small"
          aria-label="Refresh company"
          data-track-skip="true"
          sx={{
            width: 34,
            height: 34,
            color: "#5186EC",
            backgroundColor: "#F4F8FE",
            border: "1px solid #E1E8F5",
            transition: "all .18s ease",
            "&:hover": {
              backgroundColor: "#5186EC",
              color: "#fff",
              borderColor: "#5186EC",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(81,134,236,.30)",
            },
            "&.Mui-disabled": { color: "#5186EC", backgroundColor: "#F4F8FE", opacity: 1 },
          }}
        >
          {spinning ? (
            <CircularProgress size={17} thickness={5} sx={{ color: "#5186EC" }} />
          ) : (
            <RefreshIcon fontSize="small" />
          )}
        </IconButton>
        </span>
      </Tooltip>
      <Typography
        title={`Last underwriting date: ${formatLastUnderwritingDate(row?.lastUnderwritingDate, true)}`}
        sx={{ mt: 0.25, fontSize: 9.5, color: '#6B7A90', whiteSpace: 'nowrap', lineHeight: 1.15 }}
      >
        {formatLastUnderwritingDate(row?.lastUnderwritingDate)}
      </Typography>
    </Box>
  );
};

// Column header for the Refresh column, with a "NEW" badge.
const RefreshHeader = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <span>Refresh</span>
    <Box
      component="span"
      sx={{
        fontSize: 8.5,
        fontWeight: 800,
        letterSpacing: ".04em",
        color: "#0E2A5A",
        background: "#FFD34E",
        borderRadius: "5px",
        px: "5px",
        py: "1px",
        lineHeight: 1.5,
      }}
    >
      NEW
    </Box>
  </Box>
);

// Drop Down cons
const ActionsMenu = ({ row }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(row)
  const [fileType, setFileType] = useState(null)

  const handleOpen = () => setOpen(true);
  const handleCloseSR = () => setOpen(false);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action) => {
    console.log(`Action: ${action}, Row ID: ${row.id}`);
    setFileType(action)
    setOpen(true);
    handleClose();
  };

  const recommended = Array.isArray(selectedCompany?.["Recommended Action"])
    ? MitigationOptions.filter(item =>
        selectedCompany["Recommended Action"].some(
          action => action.toLowerCase() === item.key?.toLowerCase()
        )
      )
    : [];
  const availableMitigations = recommended.length > 0 ? recommended : MitigationOptions;
  const hasRecommendations = recommended.length > 0;

  return (
    <>
      {availableMitigations.length === 1 ? (
        <Button
          variant="contained"
          color="primary"
          disabled={false}
          sx={{
            background: "#5186EC 0% 0% no-repeat padding-box",
            borderRadius: "6px",
            backgroundColor: "#5186EC",
            fontSize: "13px",
            textTransform: "none",
            fontWeight: "400",
            lineHeight: "1",
            margin: "0 auto",
            padding: "8px",
            textAlign: "center",
            whiteSpace: "normal",
            wordBreak: "break-word",
            opacity: hasRecommendations ? 1 : 0.6,
          }}
          onClick={() => handleMenuItemClick(availableMitigations[0].key)}
        >
          {availableMitigations[0].value}
        </Button>
      ) : availableMitigations.length > 1 ? (
        <Button
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "12px",
            height: "30px",
            backgroundColor: "#5186EC",
            "&:hover": {
              bgcolor: "#5186EC",
            },
            width: "100%",
            maxWidth: "135px",
            margin: "0 auto",
            opacity: hasRecommendations ? 1 : 0.6,
          }}
          variant="contained"
          color="primary"
          onClick={handleClick}
          endIcon={<KeyboardArrowDownIcon />}
        >
          Mitigations
        </Button>) : null}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "8px",
          },
        }}
      >
        {availableMitigations.length > 0 ? (
          availableMitigations.map((item, index) => (
            <div key={index}>
              <MenuItem
                onClick={() => handleMenuItemClick(item.key)}
                sx={{ fontFamily: "Rubik, sans-serif" }}
              >
                {item.value}
              </MenuItem>
              {availableMitigations.length - 1 !== index && <Divider />}
            </div>
          ))
        ) : null}
      </Menu>
      {
        open && (
          <SendRequestModal
            open={open}
            onClose={handleCloseSR}
            companyInfo={{ address: "", domain: "", name: selectedCompany["Company Name"], companyId: selectedCompany.companyId, requestedLimit: selectedCompany?.requestedCreditLimit }}
            fileType={fileType}
            navigationFrom={'table'}
          />
        )}

    </>
  );
};

const REGION_COUNTRIES = {
  Africa: [
          "Algeria", "Egypt", "Libya", "Morocco", "Sudan", "Tunisia", "Western Sahara", "Burundi", "Comoros", "Djibouti", "Eritrea", "Ethiopia", "Kenya", "Madagascar", "Malawi", "Mauritius", "Mozambique", "Rwanda", "Seychelles", "Somalia", "South Sudan", "Tanzania", "Uganda", "Zambia", "Zimbabwe", "Angola", "Cameroon", "Central African Republic", "Chad", "Congo (Republic)", "Congo (Democratic Republic)", "Equatorial Guinea", "Gabon", "São Tomé and Príncipe", "Botswana", "Eswatini", "Lesotho", "Namibia", "South Africa", "Benin", "Burkina Faso", "Cape Verde", "Côte d'Ivoire", "Gambia", "Ghana", "Guinea", "Guinea-Bissau", "Liberia", "Mali", "Mauritania", "Niger", "Nigeria", "Senegal", "Sierra Leone", "Togo"
        ],
  Asia: [
          "Kazakhstan", "Kyrgyzstan", "Tajikistan", "Turkmenistan", "Uzbekistan", "China", "Japan", "Mongolia", "North Korea", "South Korea", "Taiwan", "Afghanistan", "Bangladesh", "Bhutan", "India", "Maldives", "Nepal", "Pakistan", "Sri Lanka", "Brunei", "Cambodia", "East Timor", "Indonesia", "Laos", "Malaysia", "Myanmar", "Philippines", "Singapore", "Thailand", "Vietnam"
        ],
  MiddleEast: [
          "Armenia", "Azerbaijan", "Bahrain", "Cyprus", "Georgia", "Iran", "Iraq", "Israel", "Jordan", "Kuwait", "Lebanon", "Oman", "Palestine", "Qatar", "Saudi Arabia", "Syria", "Turkey", "UAE", "Yemen"
        ],
  Europe: [
"Belarus", "Bulgaria", "Czechia", "Hungary", "Moldova", "Poland", "Romania", "Russia", "Slovakia", "Ukraine", "Denmark", "Estonia", "Finland", "Iceland", "Ireland", "Latvia", "Lithuania", "Norway", "Sweden", "United Kingdom", "Albania", "Andorra", "Bosnia and Herzegovina", "Croatia", "Greece", "Italy", "Kosovo", "Malta", "Montenegro", "North Macedonia", "Portugal", "San Marino", "Serbia", "Slovenia", "Spain", "Vatican City", "Austria", "Belgium", "France", "Germany", "Liechtenstein", "Luxembourg", "Monaco", "Netherlands", "Switzerland"
        ],
  NorthAmerica: [
       "Canada", "Mexico", "United States", "USA", "US"
        ],
  CentralAmerica: [
    "Belize", "Costa Rica", "El Salvador", "Guatemala", "Honduras", "Nicaragua", "Panama"
        ],
  Caribbean: [
    "Antigua and Barbuda", "Bahamas", "Barbados", "Cuba", "Dominica", "Dominican Republic", "Grenada", "Haiti", "Jamaica", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Trinidad and Tobago"
        ],
  SouthAmerica: [
    "Argentina", "Bolivia", "Brazil", "Chile", "Colombia", "Ecuador", "Guyana", "Paraguay", "Peru", "Suriname", "Uruguay", "Venezuela"
        ],
  AustraliaandNewZealand: [
    "Australia", "New Zealand"
        ],
  Latam: [
    "Mexico", "Guatemala", "Honduras", "El Salvador", "Nicaragua", "Costa Rica", "Panama", "Cuba", "Dominican Republic", "Haiti", "Jamaica", "Puerto Rico", "The Bahamas", "Barbados", "Grenada", "Trinidad and Tobago", "Saint Lucia", "Saint Vincent and the Grenadines", "Colombia", "Venezuela", "Guyana", "Suriname", "Brazil", "Ecuador", "Peru", "Bolivia", "Chile", "Paraguay", "Argentina", "Uruguay"
        ]
};

const geographicalRegionFilterOperator = {
  ...getGridSingleSelectOperators()[0],
  label: '=',
  value: 'regionEquals',
  getApplyFilterFn: (filterItem) => {
    if (!filterItem.value) return null;

    const selectedRegion = filterItem.value;
    const validCountries = REGION_COUNTRIES[selectedRegion] || [];

    return ({ row }) => {
      // Safely handle the country field
      const countryField = row?.country;
      if (!countryField) return false;
      
      // Convert to string and handle the format
      const countryStr = String(countryField);
      const country = countryStr.includes(',') 
        ? countryStr.split(',').pop().trim().toLowerCase()
        : countryStr.trim().toLowerCase();
      
      return validCountries.some(validCountry => 
        validCountry.toLowerCase() === country
      );
    };
  },
};

const columns = [
  {
    field: "Company Name",
    headerName: "Company Name",
    type: "string",
    flex: 1.2,
    sortable: true,
    headerAlign: "left",
    align: "left",
    renderCell: (params) => (
      <Box
        display="flex"
        alignItems="center"
        width="100%"
        sx={{ cursor: "pointer" }}
      >
        {params.row.isPublic && (
          <CircleIcon sx={{ color: "#3097D2", fontSize: 9, mr: 2 }} />
        )}
        <Typography
          noWrap
          sx={{
            fontFamily: "rubik",
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          {params.value}
        </Typography>
      </Box>
    ),
  },
  {
    field: "Paydex",
    type: "number",
    headerName: "Payment Index",
    flex: 0.5,
    align: "left",
    headerAlign: "left",
    sortable: true,
    renderHeader: (params) => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Typography
          sx={{
            whiteSpace: "normal",
            lineHeight: "1.5",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: "rubik",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {params?.colDef?.headerName || "Payment Index"}
        </Typography>
        <Tooltip
          title="An indicator that measures the timeliness of a company's payments based on its historical payment behaviour. A higher score suggests delays or late payments while a lower score indicates prompt payments."
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#5186EC",
                color: "white",
                fontSize: "12px",
              },
            },
          }}
          placement="top"
        >
          <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
        </Tooltip>
      </Box>
    ),
    renderCell: (params) => {
      const paydex = params.row["Paydex"];

      const displayValue =
        paydex != null && parseFloat(params.row["Paydex"]) !== 0
          ? normalizePaydex(params.row["Paydex"])
          : "-";

      const { darkColor: color, backgroundColor, /*smiley */ } = getScoreAttributes(displayValue);

      return <Chip
        label={displayValue}
        style={{
          cursor: "default",
          backgroundColor,
          color,
          borderRadius: "8px",
          width: "fit-content",
          height: "30px",
        }}
      />;
    },
  },
  {
    field: "Combined Score",
    type: "number",
    headerName: "Arc Score",
    flex: 0.5,
    align: "left",
    headerAlign: "left",
    sortable: true,
    renderHeader: (params) => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Typography
          sx={{
            whiteSpace: "normal",
            lineHeight: "1.5",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: "rubik",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {params.colDef.headerName}
        </Typography>
        <Tooltip
          title="An indicator that measures the timeliness of a company's payments based on its Arc and Paydex scores. A higher score suggests delays or late payments while a lower score indicates prompt payments."
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#5186EC",
                color: "white",
                fontSize: "12px",
              },
            },
          }}
          placement="top"
        >
          <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
        </Tooltip>
      </Box>
    ),
    renderCell: (params) => {
      if (isUnderwritingActive(params.row)) return <UnderwritingCell />;
      const paydex = params.row["Combined Score"];

      const displayValue =
        paydex != null && formatArcScore(params.row["Combined Score"]) !== 0
          ? formatArcScore(params.row["Combined Score"])
          : "-";

      const { darkColor: color, backgroundColor, /*smiley */ } = getScoreAttributes(displayValue);

      return <Chip
        label={displayValue}
        style={{
          cursor: "default",
          backgroundColor,
          color,
          borderRadius: "8px",
          width: "fit-content",
          height: "30px",
        }}
      />;
    },
  },
  {
    field: "External Score",
    type: "number",
    headerName: "External Score",
    flex: 0.65,
    align: "left",
    headerAlign: "left",
    sortable: true,
    renderHeader: (params) => (
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
        <Typography sx={{ whiteSpace: "normal", lineHeight: 1.5, fontFamily: "rubik", fontSize: 14, fontWeight: 600 }}>
          {params.colDef.headerName}
        </Typography>
        <Tooltip
          title="The ARC risk score calculated from external underwriting data before the customer's Payment Index is applied."
          componentsProps={{ tooltip: { sx: { backgroundColor: "#5186EC", color: "white", fontSize: "12px" } } }}
          placement="top"
        >
          <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
        </Tooltip>
      </Box>
    ),
    renderCell: (params) => {
      if (isUnderwritingActive(params.row)) return <UnderwritingCell />;
      const score = params.row["External Score"];
      const displayValue = score != null && Number(score) !== 0 ? formatArcScore(score) : "-";
      const { darkColor: color, backgroundColor } = getScoreAttributes(displayValue);
      return <Chip label={displayValue} sx={{ color, backgroundColor, borderRadius: "8px", width: "fit-content", height: "30px" }} />;
    },
  },
  {
    field: "Recommended Limit",
    headerName: "Recommended Limit",
    type: "number",
    flex: 1,
    headerAlign: "left",
    align: "left",
    sortable: true,
    renderHeader: (params) => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Typography
          sx={{
            whiteSpace: "normal",
            lineHeight: "1.5",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: "rubik",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {params.colDef.headerName}
        </Typography>
        <Tooltip
          title="Maximum Recommended Credit Limit for each company. Recommendations may change overtime or based on new information"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#5186EC",
                color: "white",
                fontSize: "12px",
              },
            },
          }}
          placement="top"
        >
          <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
        </Tooltip>
      </Box>
    ),
    renderCell: (params) => (
      <Chip
        label={
          formatCurrency(params.row["Recommended Limit"]) !== 0
            ? formatCurrency(params.row["Recommended Limit"])
            : "-"
        }
        sx={{
          color: "#1A2A56",
          backgroundColor: "#ffffff",
          // backgroundColor: "#3753A033",
          // borderRadius: "8px",
          width: "fit-content",
        }}
      />
    ),
  },
  {
    field: "country",
    headerName: "Country",
    type: "string",
    flex: 1,
    headerAlign: "left",
    align: "left",
    sortable: true,
    filterable: true,
    hideable: true,
    hide: true,
    filterOperators: [
      {
        ...getGridStringOperators()[0],
        label: "=",
      }
    ],
  },
  {
    field: "Current",
    headerName: "Current",
    type: "number",
    flex: 1,
    headerAlign: "left",
    align: "left",
    sortable: true,
    valueGetter: (params) => {
      const currentValue = params.row["Current AR Balance"];
      return currentValue ? formatCurrencyValue(currentValue) : 0;
    },
    renderHeader: (params) => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Typography
          sx={{
            whiteSpace: "normal",
            lineHeight: "1.5",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: "rubik",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {params.colDef.headerName}
        </Typography>
        <Tooltip
          title="The portion of the Accounts Receivable (AR) that is still within the payment terms."
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#5186EC",
                color: "white",
                fontSize: "12px",
              },
            }
          }
          }
          placement="top"
        >
          <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
        </Tooltip>
      </Box>
    ),
    renderCell: (params) => {
      <div style={{ cursor: "default" }}>{formatCurrency(params.row["Current AR Balance"])}</div>
    },
  },
  {
    field: "AR in arrear",
    headerName: "AR in arrear",
    type: "number",
    flex: 1,
    headerAlign: "left",
    align: "left",
    sortable: true,
    valueGetter: (params) => {
      let arBalance15Value = (params.row["AR Balance Aging 15"]) || 0;
      let arBalance30Value = (params.row["AR Balance Aging 30"]) || 0;
      let arBalance45Value = (params.row["AR Balance Aging 45"]) || 0;
      let arBalance60Value = (params.row["AR Balance Aging 60"]) || 0;
      let arBalance60PlusValue = (params.row["AR Balance Aging 60+"]) || 0;
      let arBalance90Value = (params.row["AR Balance Aging 90"]) || 0;
      let arBalance90PlusValue = (params.row["AR Balance Aging 90+"]) || 0;
      let arBalance120Value = (params.row["AR Balance Aging 120"]) || 0;
      let arBalance120PlusValue = (params.row["AR Balance Aging 120+"]) || 0;
      let arBalance180Value = (params.row["AR Balance Aging 180"]) || 0;
      let arBalance180PlusValue = (params.row["AR Balance Aging 180+"]) || 0;
      let arBalance365Value = (params.row["AR Balance Aging 365"]) || 0;
      let arBalance365PlusValue = (params.row["AR Balance Aging 365+"]) || 0;
      const finalValue = parseFloat(arBalance15Value) + parseFloat(arBalance30Value) + parseFloat(arBalance45Value) + parseFloat(arBalance60Value) + parseFloat(arBalance60PlusValue) +
        parseFloat(arBalance90Value) + parseFloat(arBalance90PlusValue) + parseFloat(arBalance120Value) + parseFloat(arBalance120PlusValue) +
        parseFloat(arBalance180Value) + parseFloat(arBalance180PlusValue) + parseFloat(arBalance365Value) + parseFloat(arBalance365PlusValue);
      return finalValue;
    },

    renderHeader: (params) => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Typography
          sx={{
            whiteSpace: "normal",
            lineHeight: "1.5",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: "rubik",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {params.colDef.headerName}
        </Typography>
        <Tooltip
          title="The Sum of receivables that are overdue"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#5186EC",
                color: "white",
                fontSize: "12px",
              },
            }
          }
          }
          placement="top"
        >
          <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
        </Tooltip>
      </Box>
    ),
    renderCell: (params) => {
      return <div style={{ cursor: "default" }}>{formatCurrency(formatCurrencyValue(params.value))}</div>;
    },
  },
  {
    field: "Total AR Balance",
    headerName: "Total AR Balance",
    type: "number",
    flex: 1,
    headerAlign: "left",
    align: "left",
    sortable: true,
    renderHeader: (params) => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Typography
          sx={{
            whiteSpace: "normal",
            lineHeight: "1.5",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: "rubik",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {params?.colDef?.headerName || "Total AR Balance"}
        </Typography>
        <Tooltip
          title="The total amount of money that have been invoiced but not yet paid."
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#5186EC",
                color: "white",
                fontSize: "12px",
              },
            },
          }}
          placement="top"
        >
          <InfoRoundedIcon sx={{ fontSize: 16, mt: 0.4 }} />
        </Tooltip>
      </Box>
    ),
    renderCell: (params) => {
      return (
        <div style={{ cursor: "default" }}>{formatCurrency(params.row["Total AR Balance"])}</div>
      )
    },
  },
  {
    // Per-company rescore — click to re-run underwriting; spins while this company is rescoring.
    field: "refresh",
    headerName: "Refresh",
    renderHeader: () => <RefreshHeader />,
    headerAlign: "center",
    align: "center",
    width: 104,
    sortable: false,
    filterable: false,
    disableExport: true,
    renderCell: (params) => <RefreshCompanyButton row={params.row} />,
  },
  {
    field: "createdAt",
    headerName: "Created Date",
    flex: 0,
    type: "date",
    headerAlign: "left",
    align: "left",
    filterable: true,
    hideable: true,
    hide: true,
    valueGetter: (params) => {
      if (!params.row.createdAt) return null;

      const date = new Date(params.row.createdAt);

      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    },
    valueFormatter: (params) => {
      if (!params.value) return "";

      return params.value.toLocaleDateString();
    },
    renderCell: (params) => params.row.createdAtFormatted,
  },
  {
    field: "countryScore",
    headerName: "Country Score",
    flex: 0,
    type: "number",
    headerAlign: "left",
    align: "left",
    filterable: true,
    hideable: true,
    hide: true,
    valueGetter: (params) => {
      
      const countryScore = params.row["Country Score"];
      return countryScore != null ? parseFloat(countryScore) : null;
    },
  },
  {
    field: "geographicalRegion",
    headerName: "Geographical Region",
    type: "singleSelect",
    valueOptions: Object.keys(REGION_COUNTRIES),
    filterable: true,
    hide: true,
    hideable: false,
    filterOperators: [geographicalRegionFilterOperator],
  }
];

const prospectsColumns = [
  {
    field: "Company Name",
    headerName: "Company Name",
    type: "string",
    flex: 1.2,
    sortable: true,
    headerAlign: "left",
    align: "left",
    renderCell: (params) => (
      <Box
        display="flex"
        alignItems="center"
        width="100%"
        sx={{ cursor: "pointer" }}
      >
        {params.row.isPublic && (
          <CircleIcon sx={{ color: "#3097D2", fontSize: 9, mr: 2 }} />
        )}
        <Typography
          noWrap
          sx={{
            fontFamily: "rubik",
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          {params.value}
        </Typography>
      </Box>
    ),
  },
  {
    field: "Requested Limit",
    type: "number",
    headerName: "Amount Requested",
    flex: 0.8,
    align: "left",
    headerAlign: "left",
    sortable: true,
    valueGetter: (params) => {
      const amountRequested = params.row["Requested Limit"];
      return amountRequested != null ? parseFloat(amountRequested) : null;
    },
    renderCell: (params) => <EditableRequestedAmount row={params.row} />,
  },
  {
    field: "Recommended Limit",
    headerName: "Recommended Limit",
    type: "number",
    flex: 1,
    headerAlign: "left",
    align: "left",
    sortable: true,
    renderHeader: (params) => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Typography
          sx={{
            whiteSpace: "normal",
            lineHeight: "1.5",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: "rubik",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {params.colDef.headerName}
        </Typography>
        <Tooltip
          title="Maximum Recommended Credit Limit for each company. Recommendations may change overtime or based on new information"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#5186EC",
                color: "white",
                fontSize: "12px",
              },
            },
          }}
          placement="top"
        >
          <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
        </Tooltip>
      </Box>
    ),
    renderCell: (params) => (
      <Chip
        label={
          formatCurrency(params.row["Recommended Limit"]) !== "$0" ? formatCurrency(params.row["Recommended Limit"]) : "-"
        }
        sx={{
          color: "#1A2A56",
          backgroundColor: "#3753A033",
          borderRadius: "8px",
          width: "fit-content",
        }}
      />
    ),
  },
  {
    field: "country",
    headerName: "Country",
    type: "string",
    flex: 1,
    headerAlign: "left",
    align: "left",
    sortable: true,
    filterable: true,
    hideable: true,
    hide: true,
    filterOperators: [
      {
        ...getGridStringOperators()[0],
        label: "=",
      }
    ],
  },
  {
    field: "Combined Score",
    headerName: "Arc Score",
    flex: 0.8,
    headerAlign: "left",
    type: "number",
    align: "left",
    sortable: true,
    valueGetter: (params) => {
      const combinedScore = params.row["Combined Score"];
      const displayScore =
        !isNaN(combinedScore) && isFinite(combinedScore)
          ? formatArcScore(combinedScore)
          : 0;
      return displayScore;
    },
    renderHeader: (params) => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Typography
          sx={{
            whiteSpace: "normal",
            lineHeight: "1.5",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: "rubik",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {params.colDef.headerName}
        </Typography>
        <Tooltip
          title="Arc Score represents the overall credit risk of a company. Like all other scores on our platform, it is scaled from 1 - 10 (1 being the safest score)."
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#5186EC",
                color: "white",
                fontSize: "12px",
              },
            },
          }}
          placement="top"
        >
          <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
        </Tooltip>
      </Box>
    ),
    renderCell: (params) => {
      if (isUnderwritingActive(params.row)) return <UnderwritingCell />;
      const combinedScore = params.row["Combined Score"];
      const displayScore =
        !isNaN(combinedScore) && isFinite(combinedScore)
          ? formatArcScore(combinedScore)
          : "-";

      const { darkColor: color, backgroundColor, smiley } = getScoreAttributes(combinedScore);

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            margin: "0 auto",
          }}
        >
          <Chip
            label={displayScore}
            sx={{
              color: color,
              backgroundColor: backgroundColor,
              borderRadius: "8px",
              width: "fit-content",
              height: "30px",
            }}
          />

          {smiley && <img src={smiley} style={{ width: "15px" }} alt="" />}
        </div>
      );
    },
  },
  {
    field: "approvedLimit",
    headerName: "Approved Limit",
    type: "number",
    flex: 0.8,
    align: "left",
    headerAlign: "left",
    sortable: true,
    valueGetter: (params) => {
      const approvedLimit = params.row["approvedLimit"];
      return approvedLimit != null ? parseFloat(approvedLimit) : null;
    },
    renderCell: (params) => {
      return <div style={{ cursor: "default" }}>{formatCurrency(params.row["approvedLimit"]) !== "$0" ? formatCurrency(params.row["approvedLimit"]) : "-"}</div>;
    },
  },
  {
    field: "actions",
    headerName: "Recommended Actions",
    headerAlign: "left",
    align: "left",
    flex: 1.3,
    sortable: false,
    filterable: false,
    renderCell: (params) => <ActionsMenu row={params.row} />,
  },
  {
    field: "refresh",
    headerName: "Refresh",
    renderHeader: () => <RefreshHeader />,
    headerAlign: "center",
    align: "center",
    width: 104,
    sortable: false,
    filterable: false,
    disableExport: true,
    renderCell: (params) => <RefreshCompanyButton row={params.row} />,
  },
  {
    field: "createdAt",
    headerName: "Created Date",
    flex: 0,
    type: "date",
    headerAlign: "left",
    align: "left",
    filterable: true,
    hideable: true,
    hide: true,
    valueGetter: (params) => {
      if (!params.row.createdAt) return null;

      const date = new Date(params.row.createdAt);

      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    },
    valueFormatter: (params) => {
      if (!params.value) return "";

      return params.value.toLocaleDateString();
    },
    renderCell: (params) => params.row.createdAtFormatted,
  },
  {
    field: "countryScore",
    headerName: "Country Score",
    type: "number",
    filterable: true,
    hideable: true,
    hide: true,
    valueGetter: (params) => {
      
      const countryScore = params.row["Country Score"];
      return countryScore != null ? parseFloat(countryScore) : null;
    },
  },
  {
    field: "geographicalRegion",
    headerName: "Geographical Region",
    type: "singleSelect",
    valueOptions: Object.keys(REGION_COUNTRIES),
    filterable: true,
    hide: true,
    hideable: false,
    filterOperators: [geographicalRegionFilterOperator],
  },
];

const preprocessData = (row) => {
  if (row && row.startsWith("Net ")) {
    row = row.substring(4);
  }
  return parseInt(row) || "-";
};

const generateCSVRows = (rows, columns, isProspects, selectedTab, agingDays, hasApprovalRules = false) => {
  let selectedColumns;

  if (isProspects) {
    selectedColumns = [
      "Company Name",
      "Requested Limit",
      "Recommended Limit",
      ...(hasApprovalRules ? ["approvedLimit"] : []),
      "Combined Score",
      "lastUnderwritingDate",
      "createdAt",
      "country",
      "Country Score",
      "geographicalRegion",
    ];
  } else if (selectedTab === 0) {
    selectedColumns = [
      "Company Name",
      "Paydex",
      "External Score",
      "Combined Score",
      "Recommended Limit",
      "Underwriting Reason",
      "lastUnderwritingDate",
      "Current AR Balance",
      "AR in arrear",
      "Total AR Balance",
      "createdAt",   
      "Country Score",
      "geographicalRegion",
      "country",
    ];
  } else {
    selectedColumns = [
      "Company Name",
      "Paydex",
      "External Score",
      "Combined Score",
      "Total AR Balance",
      "Current AR Balance",
      `AR Balance Aging ${agingDays.age1}`,
      `AR Balance Aging ${agingDays.age2}`,
      `AR Balance Aging ${agingDays.age3}`,
      "Underwriting Reason",
      "lastUnderwritingDate",
      "createdAt",
      "Country Score",
      "geographicalRegion",
      "country",
    ];
  }

  const csvRows = rows.map(row => {
    return selectedColumns.map(colName => {
      let value = row[colName];

      switch (colName) {
        case "createdAt":
          return value ? new Date(value).toLocaleDateString() : "";
        case "lastUnderwritingDate":
          return value ? formatLastUnderwritingDate(value, true) : "";
        case "Requested Limit":
        case "Recommended Limit":
        case "approvedLimit":
        case "Total AR Balance":
        case "Current AR Balance":
          return formatCurrency(value);
        case "Paydex":
        case "External Score":
        case "Combined Score":
          return value ? formatArcScore(value) : "-";
        case "countryScore":
          return value != null ? parseFloat(value).toFixed(2) : "-";
        case "geographicalRegion":
          const country = row.country;
            
          if (!country || typeof country !== 'string') {
            console.log("Country is not a string:", country, typeof country);
            return "-";
          }
            
          const countryLower = country.toLowerCase().trim();
          console.log("Processing region for country:", countryLower);
            
          const cleanCountry = countryLower.includes(',') 
            ? countryLower.split(',').pop().trim().toLowerCase()
            : countryLower;
            
          console.log("Clean country:", cleanCountry);
            
          for (const [region, countries] of Object.entries(REGION_COUNTRIES)) {
            const found = countries.some(c => c.toLowerCase().trim() === cleanCountry);
            if (found) {
              console.log("Found region:", region, "for country:", cleanCountry);
              return region;
            }
          }
          console.log("No region found for country:", cleanCountry);
          return "-";
        case "AR in arrear":
          let total = 0;
          ["15", "30", "45", "60", "60+", "90", "90+", "120", "120+", "180", "180+", "365", "365+"].forEach(age => {
            total += formatCurrencyValue(row[`AR Balance Aging ${age}`]) || 0;
          });
          return formatCurrency(total);
        case `AR Balance Aging ${agingDays.age1}`:
        case `AR Balance Aging ${agingDays.age2}`:
        case `AR Balance Aging ${agingDays.age3}`:
          const processedData = processAgingData(row, agingDays);
          const agingKey = colName.replace('AR Balance Aging ', '');
          return formatCurrency(processedData[agingKey] || 0);
        default:
          return value;
      }
    });
  });

  const headers = selectedColumns.map(colName => {
    switch (colName) {
      case "Paydex":
        return "Payment Index";
      case "Combined Score":
        return "ARC Score";
      case "External Score":
        return "External Score";
      case "Current AR Balance":
        return "Current";
      case "Underwriting Reason":
        return "Rationale";
      case "createdAt":
        return "Created Date";
      case "lastUnderwritingDate":
        return "Last underwriting date";
      case "Requested Limit":
        return "Amount Requested";
      case `AR Balance Aging ${agingDays.age1}`:
        return `Aging ${agingDays.age1}`;
      case `AR Balance Aging ${agingDays.age2}`:
        return `Aging ${agingDays.age2}`;
      case `AR Balance Aging ${agingDays.age3}`:
        return `Aging ${agingDays.age3}`;
      default:
        return colName;
    }
  });

  return [headers, ...csvRows];
};

// RFC-4180 CSV escaping: quote any field containing a comma, double-quote, or newline and
// double the internal quotes. Without this, company names with commas
// (e.g. "Banco Mercantil del Norte, S.A., Institución de Banca Múltiple, …") split across
// columns and push every following field one cell to the right.
const csvEscape = (v) => {
  const s = v == null ? "" : String(v);
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

const CustomExportButton = ({ rows, columns, isProspects, selectedTab, agingDays, hasApprovalRules = false }) => {
  const apiRef = useGridApiContext();
  const timestamp = new Date().toISOString().split("T")[0];
  const selectedTabName =
    selectedTab === 0
      ? isProspects === false
        ? "Crediarc AR Summary"
        : "Crediarc credit rating and recommendations"
      : isProspects === false
        ? "Crediarc Most risky customes"
        : "Crediarc customers at risk";

  const fileName = selectedTabName + "_" + timestamp + ".csv";

  const handleExport = () => {
    const filteredRowIds = gridFilteredSortedRowIdsSelector(apiRef.current.state);
    const filtermodel = gridFilterModelSelector(apiRef.current.state);
    const filteredRows = filteredRowIds.map((id) =>
      rows.find((row) => row.id === id)
    );
    const exportData = filtermodel.items.length ? filteredRows : rows;
    const table = generateCSVRows(exportData, columns, isProspects, selectedTab, agingDays, hasApprovalRules);
    // Build the CSV ourselves so every field is properly quoted/escaped (react-csv's CSVLink
    // was letting comma-containing names break the column alignment). ﻿ = UTF-8 BOM so
    // Excel renders accented characters (Institución, México) correctly.
    const csv = String.fromCharCode(0xFEFF) + table.map((row) => row.map(csvEscape).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Button onClick={handleExport} startIcon={<DownloadIcon />}>
      Export
    </Button>
  );
};

const CustomToolbar = ({
  searchQuery,
  handleInputChange,
  handleSearch,
  rows,
  handleDensityChange,
  handleFilterModelChange,
  setGridRef,
  paginationModel,
  isProspects,
  selectedTab,
  agingDays,
  hasApprovalRules = false
}) => {
  const textFieldRef = useRef(null);
  const apiRef = useGridApiContext();
  const density = useGridSelector(apiRef, gridDensityValueSelector);

  useEffect(() => {
    setGridRef(apiRef);
  }, [apiRef, setGridRef]);

  useEffect(() => {
    console.log("apiref", apiRef);
  }, [apiRef]);

  useEffect(() => {
    if (searchQuery && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [searchQuery]);
  useEffect(() => {
    handleDensityChange(density);
  }, [density, handleDensityChange, paginationModel]);

  useEffect(() => {
    handleFilterModelChange(apiRef.current.state.filter.filterModel.items);
  }, [apiRef, handleFilterModelChange]);

  const searchStyle = {
    background: "#FFFFFF 0% 0% no-repeat padding-box",
    boxShadow: "0px 3px 6px #00000029",
    borderRadius: "8px",
    opacity: 1,
    border: "none",
    width: "220px",
    height: "36px",
    paddingLeft: "10px",
    color: "#2F3D63",
    outline: "none",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: "none",
      },
      "& .MuiInputLabel-root": {
        transform: "translate(0, -1.5rem) scale(0.75)",
      },
    },
  };

  return (
    <GridToolbarContainer
      sx={{ display: "flex", mb: 2, justifyContent: "space-between" }}
    >
      <Box>
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <CustomExportButton
          rows={rows}
          columns={columns}
          isProspects={isProspects}
          selectedTab={selectedTab}
          agingDays={agingDays}
          hasApprovalRules={hasApprovalRules}
        />
      </Box>
      <TextField
        label="Search"
        // variant="outlined"
        size="small"
        sx={searchStyle}
        inputRef={textFieldRef}
        value={searchQuery}
        // useCallback((event) => handleInputChange(event), []
        onChange={(event) => handleInputChange(event)}
        style={{ marginBottom: 16 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleSearch}>
                <img
                  src={newSearchIcon}
                  alt="search icon"
                  width="28"
                  height="28"
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </GridToolbarContainer>
  );
};

const RecommendationTable = ({
  rows,
  riskyRows,
  showAtRisk,
  setShowAtRisk,
  filteredSummary,
  filteredProspectsValue,
  tableName,
  hasApprovalRules = false,
  showInsured = false,
  insuredLimitMap = null,
}) => {
  const [page, setPage] = useState(1);
  const [riskyPage, setRiskyPage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);
  const [searchRiskyPage, setSearchRiskyPage] = useState(1);
  const [pageSize, setPageSize] = useState(7);
  const [pageSizeRisky, setPageSizeRisky] = useState(5);
  const [paginationModel, setPaginationModel] = useState({
    pageSize,
    page: 0,
  });
  const [riskyPaginationModel, setRiskyPaginationModel] = useState({
    pageSizeRisky,
    page: 0,
  });
  const [, setDisplayedRows] = React.useState([]);
  const [/*displayedRiskyRows*/, setDisplayedRiskyRows] = React.useState([]);
  const [totalRows, setTotalRows] = useState(rows);
  const [totalRiskyRows, setTotalRiskyRows] = useState(riskyRows);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchRiskyQuery, setSearchRiskyQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState(0);
  const [density, setDensity] = useState("standard");
  const [/*filters*/, setfilters] = useState([]);
  const { isProspects, setProspects } = useProspectsAR();
  const [isCollectionAtRisk, setCollectionAtRisk] = useState(false);
  const [agingDays, /*setAgingDays*/] = useState({
    age1: "30",
    age2: "60",
    age3: "90",
  });
  const [tenantId, setTenantId] = useState();
  const [gridRef, setGridRef] = useState({});
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const URLTenant = getTenantIdFromURL();
  const currentTenant = useSelector((state) => state.userInfo.tenantId);

  // Create dynamic columns based on hasApprovalRules
  // Insured-limit column — shown only when the tenant has the insurance layer enabled. Value is the
  // company's active cover limit (carrier connected); "-" when no carrier is connected for it.
  const insuredLimitColumn = useMemo(() => ({
    field: "insuredLimit",
    headerName: "Insured Limit",
    type: "number",
    flex: 0.8,
    align: "left",
    headerAlign: "left",
    sortable: true,
    valueGetter: (params) => insuredLimitFor(insuredLimitMap, params.row.companyId),
    renderCell: (params) => {
      const lim = insuredLimitFor(insuredLimitMap, params.row.companyId);
      return <div style={{ cursor: "default" }}>{lim != null ? formatCurrency(lim) : "-"}</div>;
    },
  }), [insuredLimitMap]);

  // Insert the insured-limit column before the "actions" column (or append) when enabled.
  const withInsured = useCallback((cols) => {
    if (!showInsured) return cols;
    const idx = cols.findIndex((c) => c && c.field === "actions");
    const next = [...cols];
    if (idx >= 0) next.splice(idx, 0, insuredLimitColumn);
    else next.push(insuredLimitColumn);
    return next;
  }, [showInsured, insuredLimitColumn]);

  const dynamicColumns = useMemo(() => {
    try {
      console.log("Creating dynamic columns, hasApprovalRules:", hasApprovalRules);
      if (hasApprovalRules) {
        return withInsured(columns);
      } else {
        const filteredColumns = columns.filter(col => col && col.field && col.field !== "approvedLimit");
        console.log("Filtered columns:", filteredColumns.map(col => col.field));
        return withInsured(filteredColumns);
      }
    } catch (error) {
      console.error("Error creating dynamic columns:", error);
      return columns; // Fallback to original columns
    }
  }, [hasApprovalRules, withInsured]);

  const dynamicProspectsColumns = useMemo(() => {
    try {
      console.log("Creating dynamic prospects columns, hasApprovalRules:", hasApprovalRules);
      if (hasApprovalRules) {
        return withInsured(prospectsColumns);
      } else {
        const filteredColumns = prospectsColumns.filter(col => col && col.field && col.field !== "approvedLimit");
        console.log("Filtered prospects columns:", filteredColumns.map(col => col.field));
        return withInsured(filteredColumns);
      }
    } catch (error) {
      console.error("Error creating dynamic prospects columns:", error);
      return prospectsColumns; // Fallback to original columns
    }
  }, [hasApprovalRules, withInsured]);
  
  const selectTenantId = useCallback(() => {
    if (isAdmin) setTenantId(URLTenant);
    else setTenantId(currentTenant);
  }, [isAdmin, URLTenant, currentTenant]);

  useEffect(() => {
    selectTenantId();

    if (filteredSummary) {
      setProspects(filteredProspectsValue);
    }
  }, [filteredSummary, filteredProspectsValue, selectTenantId, setProspects]);

  useEffect(() => {
    if (selectedTab === 1) {
      setCollectionAtRisk(true);
    } else {
      setCollectionAtRisk(false);
    }
  }, [selectedTab]);

  useEffect(() => {
    console.log("rows in recco", rows.country);
    setTotalRows(rows);
    gridRef?.current?.setFilterModel({ items: [] });
  }, [isProspects, gridRef, rows]);

  // useEffect(() => {
  //    riskyRows.sort((a, b) => b["Combined Score"] - a["Combined Score"]);

  // }, [riskyRows]);

  const handleInputChange = (event) => {
    console.log(event.target.value, "85858");
    setSearchQuery(event.target.value);
  };

  React.useEffect(() => {
    if (showAtRisk) {
      setSelectedTab(1);
    }
  }, [showAtRisk]);

  const handleRiskyInputChange = (event) => {
    console.log(event.target.value, "85858");
    setSearchRiskyQuery(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    if (newValue === 0) setShowAtRisk(false);
    setSelectedTab(newValue);
  };

  const handleCellClick = async (params) => {
    if (params.field === "Company Name") {
      // Use embedded companyId if available, fallback to getCompanyData
      const companyId = params.row.companyId;
      track("company_view", { companyId, companyName: params.row["Company Name"] || null });

      // if (companyId) {
        // Direct navigation using embedded companyId - no API call needed!
    if (isAdmin) {
      window.open(
        `/tenant-dashboard/${tenantId}/company/${companyId}`,
        "_blank"
      );
    } else {
      window.open(`/company/${companyId}`, "_blank");
    }
      // } else {
      //   // Fallback to existing logic for backward compatibility
      //   console.log("No companyId found in row data, falling back to getCompanyData");
      //   let company = await getCompanyData(params.row, tenantId);

      //   if (company) {
      //     if (isAdmin) {
      //       window.open(
      //         `/tenant-dashboard/${tenantId}/company/${company.id}`,
      //         "_blank"
      //       );
      //     } else {
      //       window.open(`/tenant-dashboard/${tenantId}/company/${company.id}`, "_blank");
      //     }
      //   } else {
      //     console.log("No company found with provided Account ID or Company Name.");
      //   }
      // }
    }
  };


  const handleDensityChange = useCallback((density) => {
    console.log("Density Changed -->", density);
    const pageSizeForDensity = {
      comfortable: 5,
      standard: 7,
      compact: 10,
    };
    const newPageSize = pageSizeForDensity[density];
    setPageSize(newPageSize);
    setPageSizeRisky(newPageSize);
    if (newPageSize !== pageSize) {
      setPaginationModel((prevModel) => ({
        ...prevModel,
        pageSize: newPageSize,
        page: 0,
      }));
      setRiskyPaginationModel((prevModel) => ({
        ...prevModel,
        pageSize: newPageSize,
        page: 0,
      }));
    }
    setDensity(density)
  }, [pageSize]);

  React.useEffect(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    setDisplayedRows(rows.slice(start, end));
  }, [page, pageSize, rows, agingDays]);

  React.useEffect(() => {
    const start = (riskyPage - 1) * pageSizeRisky;
    const end = start + pageSizeRisky;
    setDisplayedRiskyRows(riskyRows.slice(start, end));
  }, [riskyPage, pageSizeRisky, riskyRows, setDisplayedRiskyRows]);

  /*const handlePageChange = (event, value) => {
    if (searchQuery) {
      setSearchPage(value);
    } else {
      setPage(value);
    }
  };*/

  /*const handleRiskyPageChange = (event, value) => {
    if (searchRiskyQuery) {
      setSearchRiskyPage(value);
    } else {
      setRiskyPage(value);
    }
  };*/

  const handleSearch = useCallback(() => {
    if (!searchQuery) {
      // Reset to the original rows if the search query is empty
      setTotalRows(rows);
      const start = (searchPage - 1) * pageSize;
      const end = start + pageSize;
      setDisplayedRows(rows.slice(start, end));
      return;
    }

    const filteredRows = rows.filter((row) => {
      const query = searchQuery.toLowerCase();
      const companyName = row["Company Name"]?.toString()?.toLowerCase();
      return companyName?.includes(query);
    });

    setTotalRows(filteredRows);

    const start = (searchPage - 1) * pageSize;
    const end = start + pageSize;
    setDisplayedRows(filteredRows.slice(start, end));
  }, [searchQuery, rows, searchPage, pageSize]);

  const handleRiskySearch = useCallback(() => {
    if (!searchRiskyQuery) {
      // Reset to the original riskyRows if the search query is empty
      setTotalRiskyRows(riskyRows);
      const start = (searchRiskyPage - 1) * pageSizeRisky;
      const end = start + pageSizeRisky;
      setDisplayedRiskyRows(riskyRows.slice(start, end));
      return;
    }

    const filteredRows = riskyRows.filter((row) => {
      const query = searchRiskyQuery.toLowerCase();
      const companyName = row["Company Name"].toString().toLowerCase();
      return companyName.includes(query);
    });

    setTotalRiskyRows(filteredRows);

    const start = (searchRiskyPage - 1) * pageSizeRisky;
    const end = start + pageSizeRisky;
    setDisplayedRiskyRows(filteredRows.slice(start, end));
  }, [searchRiskyQuery, riskyRows, searchRiskyPage, pageSizeRisky, setDisplayedRiskyRows]);

  React.useEffect(() => {
    if (searchQuery) {
      handleSearch();
    } else {
      const end = pageSize;
      setDisplayedRows(rows.slice(0, end));
      setTotalRows(rows);
      setPage(1);
      setSearchPage(1);
    }
  }, [searchPage, searchQuery, handleSearch, pageSize, rows]);

  React.useEffect(() => {
    if (searchRiskyQuery) {
      handleRiskySearch();
    } else {
      const end = pageSizeRisky;
      setDisplayedRiskyRows(riskyRows.slice(0, end));
      setTotalRiskyRows(riskyRows);
      setRiskyPage(1);
      setSearchRiskyPage(1);
    }
  }, [searchRiskyPage, searchRiskyQuery, handleRiskySearch, pageSizeRisky, riskyRows, setDisplayedRiskyRows]);

  const handleFilterModelChange = useCallback((value) => {
    console.log("filter value", value);
    setfilters(value);
  }, [setfilters]);

  const riskyColumns = [
    {
      field: "Company Name",
      headerName: "Company Name",
      type: "string",
      flex: 1.2,
      sortable: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          width="100%"
          sx={{ cursor: "pointer" }}
        >
          {params.row.isPublic && (
            <CircleIcon sx={{ color: "#3097D2", fontSize: 9, mr: 2 }} />
          )}
          <Typography
            noWrap
            sx={{
              fontFamily: "rubik",
              fontWeight: 500,
              fontSize: "14px",
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "Paydex",
      type: "number",
      headerName: "Payment Index",
      flex: 0.8,
      align: "left",
      headerAlign: "left",
      sortable: true,
      renderHeader: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Typography
            sx={{
              whiteSpace: "normal",
              lineHeight: "1.5",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "rubik",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {params.colDef.headerName}
          </Typography>
          <Tooltip
            title="An indicator that measures the timeliness of a company's payments based on its historical payment behaviour. A higher score suggests delays or late payments while a lower score indicates prompt payments."
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#5186EC",
                  color: "white",
                  fontSize: "12px",
                },
              },
            }}
            placement="top"
          >
            <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => {
        const paydex = params.row["Paydex"];
        const displayValue =
          paydex != null && parseFloat(params.row["Paydex"]) !== 0
            ? normalizePaydex(params.row["Paydex"])
            : "-";

        const { darkColor: color, backgroundColor, /*smiley */ } = getScoreAttributes(displayValue);

        return <Chip
          label={displayValue}
          style={{
            cursor: "default",
            backgroundColor,
            color,
            borderRadius: "8px",
            width: "fit-content",
            height: "30px",
          }}
        />;
      },
    },
    {
      field: "Combined Score",
      headerName: "Arc Score",
      flex: 0.8,
      headerAlign: "left",
      type: "number",
      align: "left",
      sortable: true,
      valueGetter: (params) => {
        const combinedScore = params.row["Combined Score"];
        const displayScore =
          !isNaN(combinedScore) && isFinite(combinedScore)
            ? formatArcScore(combinedScore)
            : 0;
        return displayScore;
      },
      renderHeader: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Typography
            sx={{
              whiteSpace: "normal",
              lineHeight: "1.5",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "rubik",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {params.colDef.headerName}
          </Typography>
          <Tooltip
            title="Arc Score represents the overall credit risk of a company. Like all other scores on our platform, it is scaled from 1 - 10 (1 being the safest score)."
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#5186EC",
                  color: "white",
                  fontSize: "12px",
                },
              },
            }}
            placement="top"
          >
            <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => {
        const combinedScore = params.row["Combined Score"];
        const displayScore =
          !isNaN(combinedScore) && isFinite(combinedScore)
            ? formatArcScore(combinedScore)
            : "-";

        const { darkColor: color, backgroundColor, smiley } = getScoreAttributes(combinedScore);

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              margin: "0 auto",
            }}
          >
            <Chip
              label={constrainValue(displayScore, 1, 10)}
              sx={{
                color: color,
                backgroundColor: backgroundColor,
                borderRadius: "8px",
                width: "fit-content",
                height: "30px",
              }}
            />

            <img src={smiley} style={{ width: "15px" }} alt="" />
          </div>
        );
      },
    },
    {
      field: "Total AR Balance",
      headerName: "Total AR Balance",
      type: "number",
      flex: 1,
      headerAlign: "left",
      align: "left",
      sortable: true,
      renderHeader: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Typography
            sx={{
              whiteSpace: "normal",
              lineHeight: "1.5",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "rubik",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {params.colDef.headerName}
          </Typography>
          <Tooltip
            title="The total amount of money that have been invoiced but not yet paid."
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#5186EC",
                  color: "white",
                  fontSize: "12px",
                },
              },
            }}
            placement="top"
          >
            <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => (
        <Chip
          label={formatCurrency(params.row["Total AR Balance"])}
          sx={{
            color: "#1A2A56",
            backgroundColor: "#ffffff",
            // backgroundColor: "#3753A033",
            // borderRadius: "8px",
            width: "fit-content",
          }}
        />
      ),
    },
    {
      field: "Current AR Balance",
      headerName: "Current",
      type: "number",
      flex: 1,
      headerAlign: "left",
      align: "left",
      sortable: true,
      renderHeader: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Typography
            sx={{
              whiteSpace: "normal",
              lineHeight: "1.5",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "rubik",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {params.colDef.headerName}
          </Typography>
          <Tooltip
            title="The portion of the Accounts Receivable (AR) that is still within the payment terms."
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#5186EC",
                  color: "white",
                  fontSize: "12px",
                },
              },
            }}
            placement="top"
          >
            <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => (
        <Chip
          label={formatCurrency(params.row["Current AR Balance"])}
          sx={{
            color: "#1A2A56",
            backgroundColor: "#ffffff",
            // backgroundColor: "#3753A033",
            // borderRadius: "8px",
            width: "fit-content",
          }}
        />
      ),
    },
    {
      field: `AR Balance Aging ${agingDays.age1}`,
      headerName: `Aging ${agingDays.age1}`,
      type: "number",
      flex: 1,
      headerAlign: "left",
      align: "left",
      sortable: true,
      valueGetter: (params) => {
        const processedData = processAgingData(params.row, agingDays);
        return processedData[agingDays.age1] || 0;
      },
      renderHeader: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Typography
            sx={{
              whiteSpace: "normal",
              lineHeight: "1.5",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "rubik",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {params.colDef.headerName}
          </Typography>
          <Tooltip
            title={`The amount of receivables that are up to ${agingDays.age1} days past the due date.`}
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#5186EC",
                  color: "white",
                  fontSize: "12px",
                },
              },
            }}
            placement="top"
          >
            <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => {
        const value = params.value || 0;
        return (
          <Chip
            label={formatCurrency(value)}
            sx={{
              color: "#1A2A56",
              backgroundColor: "#ffffff",
              width: "fit-content",
            }}
          />
        )
      },
    },
    {
      field: `AR Balance Aging ${agingDays.age2}`,
      headerName: `Aging ${agingDays.age2}`,
      type: "number",
      flex: 1,
      headerAlign: "left",
      align: "left",
      sortable: true,
      valueGetter: (params) => {
        const processedData = processAgingData(params.row, agingDays);
        return processedData[agingDays.age2] || 0;
      },
      renderHeader: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Typography
            sx={{
              whiteSpace: "normal",
              lineHeight: "1.5",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "rubik",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {params.colDef.headerName}
          </Typography>
          <Tooltip
            title={`The amount of receivables that are ${parseInt(agingDays.age1) + 1} to ${agingDays.age2} days past the due date.`}
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#5186EC",
                  color: "white",
                  fontSize: "12px",
                },
              },
            }}
            placement="top"
          >
            <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => {
        const value = params.value || 0;
        return (
          <Chip
            label={formatCurrency(value)}
            sx={{
              color: "#1A2A56",
              backgroundColor: "#ffffff",
              width: "fit-content",
            }}
          />
        );
      },
    },
    {
      field: `AR Balance Aging ${agingDays.age3}`,
      headerName: `Aging ${agingDays.age3}`,
      type: "number",
      flex: 1,
      headerAlign: "left",
      align: "left",
      sortable: true,
      valueGetter: (params) => {
        const processedData = processAgingData(params.row, agingDays);
        return processedData[agingDays.age3] || 0;
      },
      renderHeader: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Typography
            sx={{
              whiteSpace: "normal",
              lineHeight: "1.5",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "rubik",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {params.colDef.headerName}
          </Typography>
          <Tooltip
            title={`The amount of receivables that are ${parseInt(agingDays.age2) + 1} to ${agingDays.age3} days past the due date.`}
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#5186EC",
                  color: "white",
                  fontSize: "12px",
                },
              },
            }}
            placement="top"
          >
            <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: "5px" }} />
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => {
        const value = params.value || 0;
        return (
          <Chip
            label={formatCurrency(value)}
            sx={{
              color: "#1A2A56",
              backgroundColor: "#ffffff",
              width: "fit-content",
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Recommended Actions",
      headerAlign: "left",
      align: "left",
      flex: 1,
      sortable: false,
      renderCell: (params) => <ActionsMenu row={params.row} />,
    },
    {
      field: "refresh",
      headerName: "Refresh",
      renderHeader: () => <RefreshHeader />,
      headerAlign: "center",
      align: "center",
      width: 104,
      sortable: false,
      filterable: false,
      disableExport: true,
      renderCell: (params) => <RefreshCompanyButton row={params.row} />,
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      flex: 0,
      type: "date",
      headerAlign: "left",
      align: "left",
      filterable: true,
      hideable: true,
      hide: true,
      valueGetter: (params) => {
        if (!params.row?.createdAt) return null;

        const date = new Date(params.row?.createdAt);

        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
      },
      valueFormatter: (params) => {
        if (!params.value) return "";

        return params.value.toLocaleDateString();
      },
      renderCell: (params) => params.row.createdAtFormatted,
    },
    {
      field: "countryScore",
      headerName: "Country Score",
      flex: 0,
      type: "number",
      headerAlign: "left",
      align: "left",
      filterable: true,
      hideable: true,
      hide: true,
      valueGetter: (params) => {
        
        const countryScore = params.row["Country Score"];
        return countryScore != null ? parseFloat(countryScore) : null;
      },
    },
    {
      field: "geographicalRegion",
      headerName: "Geographical Region",
      type: "singleSelect",
      valueOptions: Object.keys(REGION_COUNTRIES),
      filterable: true,
      hide: true,
      hideable: false,
      filterOperators: [geographicalRegionFilterOperator],
    },
    {
      field: "country",
      headerName: "Country",
      type: "string",
      flex: 1,
      headerAlign: "left",
      align: "left",
      filterable: true,
      hideable: true,
      hide: true,
      filterOperators: [
        {
          ...getGridStringOperators()[0], 
          label: "=",
        }
      ],
    }
  ];

  // Create dynamic risky columns based on hasApprovalRules
  const dynamicRiskyColumns = useMemo(() => {
    try {
      if (hasApprovalRules) {
        return withInsured(riskyColumns);
      } else {
        return withInsured(riskyColumns.filter(col => col && col.field && col.field !== "approvedLimit"));
      }
    } catch (error) {
      console.error("Error creating dynamic risky columns:", error);
      return riskyColumns; // Fallback to original columns
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasApprovalRules, withInsured, riskyColumns]);

  const getDetailPanelHeight = () => (isProspects ? 130 : 200);

  const getDetailPanelContent = (params, isCollectionAtRisk, isProspects) => {
    const { row } = params;

    const isValidScore = (score) => !!score;
    const processedAgingData = processAgingData(row, agingDays);

    return (
      <Box>
        {isProspects ? null : (
          <>
            {/* AR Balance Aging Section */}
            {!isCollectionAtRisk && !isProspects && (
              <Grid container sx={{ mt: 1, paddingLeft: "30px", height: 75 }}>
                <Grid
                  item
                  sm={2}
                  container
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      sx={{
                        fontFamily: "Rubik",
                        fontStyle: "normal",
                        fontSize: "14px",
                        fontWeight: 600,
                        mr: 2,
                      }}
                    >
                      AR Balance
                    </Typography>
                  </Box>
                </Grid>
                <Grid item sm={10}>
                  <Box
                    container
                    spacing={1}
                    sx={{
                      height: "100%",
                      overflowX: "auto",
                      whiteSpace: "nowrap",
                      margin: "auto 0",
                      display: "flex",
                      justifyContent: "space-around",
                      alignItems: "center",
                    }}
                  >
                    {[
                      {
                        label: `Current`,
                        value: row[`Current AR Balance`],
                      },
                      {
                        label: `Aging ${agingDays.age1}`,
                        value: processedAgingData[agingDays.age1],
                      },
                      {
                        label: `Aging ${agingDays.age2}`,
                        value: processedAgingData[agingDays.age2],
                      },
                      {
                        label: `Aging ${agingDays.age3}`,
                        value: processedAgingData[agingDays.age3],
                      },
                      {
                        label: `Total AR Balance`,
                        value: row[`Total AR Balance`],
                      },
                    ].map(
                      ({ label, value }) =>
                        value != null && (
                          <Box item xs={4} key={label}>
                            <Box display="flex" alignItems="center">
                              <Typography
                                sx={{
                                  fontFamily: "Rubik",
                                  fontStyle: "normal",
                                  fontSize: "14px",
                                  width: "max-content",
                                  marginRight: "5px",
                                }}
                              >
                                {label}:
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "Rubik",
                                  fontStyle: "normal",
                                  fontSize: "14px",
                                  width: "max-content",
                                  marginRight: "5px",
                                }}
                              >
                                {formatCurrency(value)}
                              </Typography>
                            </Box>
                          </Box>
                        )
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
            {/* Recommended Limit Section */}
            {isCollectionAtRisk && !isProspects && (
              <Grid container sx={{ mt: 2, paddingLeft: "30px", height: 75 }}>
                <Grid
                  item
                  sm={2}
                  container
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      sx={{
                        fontFamily: "Rubik",
                        fontStyle: "normal",
                        fontSize: "14px",
                        fontWeight: 600,
                        mr: 2,
                      }}
                    >
                      Recommendations
                    </Typography>
                  </Box>
                </Grid>
                {[
                  {
                    label: "Recommended Limit",
                    value: row["Recommended Limit"],
                  },
                  {
                    label: "Payment Terms",
                    value: preprocessData(row["Recommended Net"]),
                  },
                ].map(
                  ({ label, value }) =>
                    value != null && (
                      <Grid item xs={5} key={label}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          sx={{ height: "100%" }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Rubik",
                              fontStyle: "normal",
                              fontSize: "14px",
                              width: "max-content",
                              marginRight: "5px",
                            }}
                          >
                            {label}:
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Rubik",
                              fontStyle: "normal",
                              fontSize: "14px",
                              width: "max-content",
                              marginRight: "5px",
                            }}
                          >
                            {parseInt(value)
                              ? parseInt(value).toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                                maximumFractionDigits: 0,
                              })
                              : value}
                          </Typography>
                        </Box>
                      </Grid>
                    )
                )}
              </Grid>
            )}
            <Divider />
          </>
        )}
        <Grid container mt={3} sx={{ padding: "0 10px", height: 75 }}>
          {/* Arc Score Section */}
          <Grid
            item
            sm={3}
            container
            alignItems="center"
            justifyContent="center"
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                sx={{
                  fontFamily: "Rubik",
                  fontStyle: "normal",
                  fontSize: "14px",
                  fontWeight: 600,
                  mr: 2,
                }}
              >
                Arc Score
              </Typography>
              <Box height="60px" width="60px">
                <DoughnutChart row={row} />
              </Box>
            </Box>
          </Grid>
          <Grid item sm={9}>
            <Grid
              container
              spacing={1}
              sx={{ overflowX: "auto", whiteSpace: "nowrap" }}
            >
              {[
                {
                  label: "Risk Segment",
                  color: "#2F3D63",
                  value: row["Risk Segment"] ? parseFloat(row["Risk Segment"]).toFixed(1) : null,
                },
                {
                  label: "Pulse Index",
                  color: "#5186EC",
                  value: row["Alternative Score"] ? parseFloat(row["Alternative Score"]).toFixed(1) : null,
                },
                {
                  label: "Deliquency Score",
                  color: "#F25A48",
                  value: row["Deliquency Score"] ? parseFloat(row["Deliquency Score"]).toFixed(1) : null,
                },
                {
                  label: "Legal Score",
                  color: "#3AA623",
                  value: row["Legal Score"] ? parseFloat(row["Legal Score"]).toFixed(1) : null,
                },
                {
                  label: "Failure Score",
                  color: "#E7BF3C",
                  value: row["Failure Score"] ? parseFloat(row["Failure Score"]).toFixed(1) : null,
                },
                {
                  label: "Sector Score",
                  color: "#3AA629",
                  value: row["Sector Score"] ? parseFloat(row["Sector Score"]).toFixed(1) : null,
                },
                {
                  label: "Country Score",
                  color: "#2F3D63",
                  value: row["Country Score"] ? parseFloat(row["Country Score"]).toFixed(1) : null,
                },
                {
                  label: "Altman Z Score",
                  color: "#F248B4",
                  value: row["Altman Z Score"] ? parseFloat(row["Altman Z Score"]).toFixed(1) : null,
                },
              ].map(
                ({ label, color, value }) =>
                  isValidScore(value) &&
                  !isNaN(value) && (
                    <Grid item xs={4} key={label}>
                      <Box display="flex" alignItems="center">
                        <CircleIcon
                          sx={{
                            width: 10,
                            height: 10,
                            position: "relative",
                            right: 5,
                            color,
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: "Rubik",
                            fontStyle: "normal",
                            fontSize: "14px",
                            width: "max-content",
                            marginRight: "5px",
                          }}
                        >
                          {label}:
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Rubik",
                            fontStyle: "normal",
                            fontSize: "14px",
                            width: "max-content",
                            marginRight: "5px",
                          }}
                        >
                          {value}
                        </Typography>
                      </Box>
                    </Grid>
                  )
              )}
              {/* Rationale Section - Integrated into the same grid */}
              {isProspects && (
                <Grid item xs={12}>
                  <Box display="flex" alignItems="flex-start" sx={{ mt: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: "Rubik",
                        fontStyle: "normal",
                        fontSize: "14px",
                        fontWeight: 600,
                        width: "max-content",
                        marginRight: "10px",
                        minWidth: "80px",
                      }}
                    >
                      Rationale:
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Rubik",
                        fontStyle: "normal",
                        fontSize: "14px",
                        lineHeight: "1.4",
                        whiteSpace: "pre-wrap",
                        wordWrap: "break-word",
                        flex: 1,
                      }}
                    >
                      {(() => {
                        let reason = "";
                        if (row["Recommended Limit"] === 2500000) {
                          reason = "Limited based on Net Income from Continuing Operations Trend";
                        } else {
                          reason = row["Underwriting Reason"] || "No rationale available";
                        }
                        return reason;
                      })()}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Card
      variant="outlined"
      style={{
        height: "fit-content",
        margin: "15px 0px 0px",
        padding: "10px 10px 10px 10px",
        boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
        position: "relative",
      }}
    >
      <div style={{ height: "100%", width: "100%" }}>
        {filteredSummary ? (
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              color: "#1A2B56",
              margin: "0 auto",
              width: "100%",
            }}
          >
            {tableName}
          </Typography>
        ) : (
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            TabIndicatorProps={{
              style: {
                backgroundColor: ColorEnum.DARK_BLUE,
                height: 4,
                borderRadius: "8px",
              },
            }}
            sx={{
              borderBottom: 0.1,
              borderBottomColor: "#dbd9d9",
              mb: 2,
            }}
          >
            <Tab
              label={
                isProspects === false
                  ? "AR Summary"
                  : "Credit Ratings & Recommendations"
              }
              style={{
                textTransform: "capitalize",
                color: selectedTab === 0 ? "#1A2A56" : "grey",
                fontWeight: selectedTab === 0 ? "500" : "400",
                fontSize: "15px",
              }}
            />
            <Tab
              label={
                isProspects === false
                  ? "Most risky customes"
                  : "Customers at Risk"
              }
              style={{
                textTransform: "capitalize",
                color: selectedTab === 1 ? "#1A2A56" : "grey",
                fontWeight: selectedTab === 1 ? "500" : "400",
                fontSize: "15px",
              }}
            />
          </Tabs>
        )}
        {selectedTab === 0 && (
          <DataGridPremium
            rows={searchQuery ? totalRows : rows}
            columns={isProspects ? dynamicProspectsColumns : dynamicColumns}
            getRowId={(row) => parseInt(row.id)}
            getDetailPanelHeight={getDetailPanelHeight}
            getDetailPanelContent={(params) =>
              getDetailPanelContent(
                params,
                isCollectionAtRisk,
                filteredSummary ? filteredProspectsValue : isProspects
              )
            }
            onCellClick={handleCellClick}
            disableColumnMenu={true}
            disableColumnResize={false}
            onFilterModelChange={handleFilterModelChange}
            pagination
            initialState={{
              columns: {
                columnVisibilityModel: {
                  createdAt: false,
                  countryScore: false,
                  geographicalRegion: false,
                  country: false,
                },
                pagination: {
                  paginationModel: { pageSize: 9, page: 0 },
                },
              },
            }}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            slots={{
              toolbar: () => (
                <CustomToolbar
                  searchQuery={searchQuery}
                  handleInputChange={handleInputChange}
                  handleSearch={handleSearch}
                  rows={searchQuery ? totalRows : rows}
                  handleDensityChange={handleDensityChange}
                  handleFilterModelChange={handleFilterModelChange}
                  setGridRef={setGridRef}
                  paginationModel={paginationModel}
                  isProspects={isProspects}
                  selectedTab={selectedTab}
                  agingDays={agingDays}
                  hasApprovalRules={hasApprovalRules}
                />
              ),
              footer: CustomFooter,
            }}
            density={density}
            slotProps={{
              footer: {
                setPaginationModel: setPaginationModel,
                paginationModel: paginationModel,
                onPaginationModelChange: setPaginationModel,
                density: density
              },
            }}
            sx={LocalStyle.dataGrid}
          />
        )}

        {selectedTab === 1 && (
          <DataGridPremium
            rows={searchRiskyQuery ? totalRiskyRows : riskyRows}
            columns={isProspects ? dynamicProspectsColumns : dynamicRiskyColumns}
            getRowId={(row) => parseInt(row.id)}
            getDetailPanelHeight={getDetailPanelHeight}
            getDetailPanelContent={(params) =>
              getDetailPanelContent(
                params,
                isCollectionAtRisk,
                filteredSummary ? filteredProspectsValue : isProspects
              )
            }
            onCellClick={handleCellClick}
            disableColumnMenu={true}
            disableColumnResize={false}
            onFilterModelChange={handleFilterModelChange}
            pagination
            initialState={{
              columns: {
                columnVisibilityModel: {
                  createdAt: false,
                  countryScore: false,
                  geographicalRegion: false,
                  country: false,
                },
              },
            }}
            paginationModel={riskyPaginationModel}
            onPaginationModelChange={setRiskyPaginationModel}
            slots={{
              toolbar: () => (
                <CustomToolbar
                  searchQuery={searchRiskyQuery}
                  handleInputChange={handleRiskyInputChange}
                  handleSearch={handleRiskySearch}
                  rows={searchRiskyQuery ? totalRiskyRows : riskyRows}
                  handleDensityChange={handleDensityChange}
                  handleFilterModelChange={handleFilterModelChange}
                  setGridRef={setGridRef}
                  paginationModel={riskyPaginationModel}
                  isProspects={isProspects}
                  selectedTab={selectedTab}
                  agingDays={agingDays}
                  hasApprovalRules={hasApprovalRules}
                />
              ),
              footer: CustomFooter,
            }}
            density={density}
            slotProps={{
              footer: {
                setPaginationModel: setRiskyPaginationModel,
                paginationModel: riskyPaginationModel,
                onPaginationModelChange: setRiskyPaginationModel,
                density: density
              },
            }}
            sx={LocalStyle.dataGrid}
          />
        )}
      </div>
    </Card>
  );
};

export default RecommendationTable;
